import api, { getStorageUrl } from './api';

const STORAGE_PREFIX = 'storage/';
const ABS_STORAGE_PREFIX = '/storage/';

const LOGO_KEYS = [
  'mainAppLogo',
  'slipLogoUrl',
  'websiteLogo',
  'websiteFooterLogo',
  'websiteLoadingLogo',
  'userPanelLogo',
];

const SETTING_KEYS = [
  'mainAppLogo',
  'appTagline',
  'gstEnabled',
  'gstRate',
  'invoiceTerms',
  'bankDetails',
  'deliveryFromAddress',
  'deliveryFooter',
  'slipLogoUrl',
  'slipWebsiteUrl',
  'slipCompanyPhone',
  'slipCompanyMobile',
  'slipLogoWidth',
  'slipLogoHeight',
  'slipPrinterMode',
  'slipA4PerPage',
  'websiteLogo',
  'websiteFooterLogo',
  'websiteLoadingLogo',
  'userPanelLogo',
  'websiteDomain',
  'websitePhone',
  'websiteEmail',
  'websiteWhatsapp',
  'socialFacebook',
  'socialInstagram',
  'socialYoutube',
  'socialTwitter',
  'socialWhatsapp',
  'officeCardTitle',
  'officeCardNote',
  'officeCardLocation',
  'officeMapUrl',
  'officeHqTitle',
  'officeHqNote',
  'officeHqAddress',
  'contactTeam',
];

export const toAbsoluteStorage = (value) => {
  if (!value || typeof value !== 'string') return value;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith(ABS_STORAGE_PREFIX)) {
    return getStorageUrl(value.slice(ABS_STORAGE_PREFIX.length));
  }
  if (value.startsWith(STORAGE_PREFIX)) {
    return getStorageUrl(value.slice(STORAGE_PREFIX.length));
  }
  if (value.startsWith('/')) {
    if (value.startsWith('/storage/')) {
      return getStorageUrl(value.slice('/storage/'.length));
    }
    return value;
  }
  return value;
};

export const BRAND_CACHE_KEY = 'flare_site_brand_v1';
export const ADMIN_SETTINGS_CACHE_KEY = 'flare_admin_settings_v1';

export function readJsonCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function writeJsonCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota */
  }
}

export const DEFAULT_CONTACT_TEAM = [
  { name: '', title: 'Chief Executive Officer', email: '', phone: '', photo: '' },
  { name: '', title: 'Director', email: '', phone: '', photo: '' },
  { name: '', title: 'Head of Operations', email: '', phone: '', photo: '' },
  { name: '', title: 'Customer Support', email: '', phone: '', photo: '' },
];

export const padContactTeam = (raw) => {
  const src = Array.isArray(raw) ? raw : [];
  const out = [0, 1, 2, 3].map((i) => {
    const row = src[i] && typeof src[i] === 'object' ? src[i] : {};
    const photo = row.photo ? toAbsoluteStorage(row.photo) : '';
    return {
      name: row.name || '',
      title: row.title || DEFAULT_CONTACT_TEAM[i].title,
      email: row.email || '',
      phone: row.phone || '',
      photo: photo || '',
    };
  });
  return out;
};

const normalizeSettings = (s = {}) =>
  SETTING_KEYS.reduce((acc, key) => {
    let v = s[key];
    if (key === 'contactTeam') {
      acc[key] = padContactTeam(v);
      return acc;
    }
    if (v === undefined || v === null || v === '') return acc;
    if (LOGO_KEYS.includes(key)) v = toAbsoluteStorage(v);
    acc[key] = v;
    return acc;
  }, {});

const PUBLIC_BRAND_KEYS = [
  'websiteLogo',
  'websiteFooterLogo',
  'websiteLoadingLogo',
  'userPanelLogo',
  'websiteDomain',
  'websitePhone',
  'websiteEmail',
  'websiteWhatsapp',
  'socialFacebook',
  'socialInstagram',
  'socialYoutube',
  'socialTwitter',
  'socialWhatsapp',
  'officeCardTitle',
  'officeCardNote',
  'officeCardLocation',
  'officeMapUrl',
  'officeHqTitle',
  'officeHqNote',
  'officeHqAddress',
  'contactTeam',
];

const cacheBrandFromSettings = (normalized) => {
  const brand = {};
  PUBLIC_BRAND_KEYS.forEach((key) => {
    if (normalized[key] !== undefined && normalized[key] !== null && normalized[key] !== '') {
      brand[key] = normalized[key];
    } else if (key === 'contactTeam' && Array.isArray(normalized[key])) {
      brand[key] = normalized[key];
    }
  });
  if (Object.keys(brand).length) writeJsonCache(BRAND_CACHE_KEY, brand);
};

export const settingsApi = {
  async getSettings() {
    const resp = (await api.get('/settings')) || {};
    const normalized = normalizeSettings(resp.data?.settings || {});
    writeJsonCache(ADMIN_SETTINGS_CACHE_KEY, normalized);
    cacheBrandFromSettings(normalized);
    return normalized;
  },

  async getPublicBranding() {
    const resp = (await api.get('/settings/branding')) || {};
    const b = resp.data?.branding || {};
    const out = {};
    Object.keys(b).forEach((key) => {
      let v = b[key];
      if (key === 'contactTeam') {
        out[key] = padContactTeam(v);
        return;
      }
      if (v === undefined || v === null || v === '') return;
      if (LOGO_KEYS.includes(key)) v = toAbsoluteStorage(v);
      out[key] = v;
    });
    if (Object.keys(out).length) writeJsonCache(BRAND_CACHE_KEY, out);
    return out;
  },

  async saveSettings(partialObj) {
    const payload = {};
    for (const k of Object.keys(partialObj)) {
      if (!SETTING_KEYS.includes(k)) continue;
      const v = partialObj[k];
      if (v === undefined) continue;
      payload[k] = v;
    }
    const resp = (await api.put('/settings', payload)) || {};
    const normalized = normalizeSettings(resp.data?.settings || {});
    writeJsonCache(ADMIN_SETTINGS_CACHE_KEY, normalized);
    cacheBrandFromSettings(normalized);
    return normalized;
  },

  async uploadImage(fieldName, file) {
    const form = new FormData();
    form.append('field', fieldName);
    form.append('image', file);
    const resp = (await api.post('/settings/upload-image', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })) || {};
    const data = resp.data || {};
    return { rawUrl: data?.url, displayUrl: toAbsoluteStorage(data?.url), path: data?.path };
  },

  keys: [...SETTING_KEYS],
  logoKeys: [...LOGO_KEYS],
};

export default settingsApi;
